import { ENTITY_STORES, SyncQueueStore, ENTITY_LIST } from './db';
import session from '../utils/session';
import logger from '../utils/logger';

class OfflineStorage {
  constructor() {
    this._currentOwner = null;
  }

  getCurrentOwner() {
    if (this._currentOwner) return this._currentOwner;
    const user = session.getUser();
    if (user) {
      this._currentOwner = user.sub || user.id || user._id;
    }
    return this._currentOwner;
  }

  setOwner(ownerId) {
    this._currentOwner = ownerId;
  }

  clearOwner() {
    this._currentOwner = null;
  }

  _getStore(entityType) {
    const store = ENTITY_STORES[entityType];
    if (!store) throw new Error(`Unknown entity type: ${entityType}`);
    return store;
  }

  async getAll(entityType) {
    const store = this._getStore(entityType);
    const owner = this.getCurrentOwner();
    if (!owner) return [];
    try {
      return await store.getAllByOwner(owner);
    } catch (err) {
      logger.error(`OfflineStorage.getAll(${entityType}) failed`, err, 'Offline');
      return [];
    }
  }

  async getById(entityType, localId) {
    const store = this._getStore(entityType);
    return store.get(localId);
  }

  async getByServerId(entityType, serverId) {
    const store = this._getStore(entityType);
    const owner = this.getCurrentOwner();
    if (!owner) return null;
    try {
      const records = await store.getAllByOwner(owner);
      const matchesServerId = (value) =>
        value !== undefined && value !== null && String(value) === String(serverId);

      // A few APIs address records by a business `id` while IndexedDB caches
      // the same record by its Mongo `_id`.  Treat either server identifier as
      // the same record so successful online updates and deletes affect the
      // row that offline mode will later read.
      return records.find((record) =>
        matchesServerId(record.serverId) ||
        matchesServerId(record.data?._id) ||
        matchesServerId(record.data?.id)
      ) || null;
    } catch {
      return null;
    }
  }

  async save(entityType, data, options = {}) {
    const owner = this.getCurrentOwner();
    if (!owner) throw new Error('User not authenticated');

    const store = this._getStore(entityType);

    // Use existing localId if data has an _id that matches an existing record,
    // otherwise use data.localId, otherwise generate a new one.
    let localId = data.localId;
    let existing = null;
    if (!localId && (data._id || data.id)) {
      const serverId = data._id || data.id;
      const existingByServerId = await this.getByServerId(entityType, serverId);
      if (existingByServerId) {
        localId = existingByServerId.localId;
        existing = existingByServerId;
      }
    }
    if (!localId) {
      localId = crypto.randomUUID
        ? crypto.randomUUID()
        : 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    if (!existing) {
      existing = await store.get(localId);
    }

    const now = Date.now();
    const version = existing ? (existing.version || 0) + 1 : 1;

    const record = {
      localId,
      owner,
      data: { ...data },
      version,
      serverId: existing?.serverId || data.serverId || null,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      syncedAt: existing?.syncedAt || null,
    };

    await store.put(record);

    const action = existing ? 'update' : 'create';
    await SyncQueueStore.add({
      localId,
      entityType,
      action,
      data: record.data,
      version,
      owner,
      serverId: record.serverId,
      status: 'pending',
      retryCount: 0,
      createdAt: now,
    });

    return record;
  }

  async remove(entityType, localId, options = {}) {
    const owner = this.getCurrentOwner();
    if (!owner) throw new Error('User not authenticated');

    const store = this._getStore(entityType);
    let existing = await store.get(localId);

    // If not found by key (localId), try looking up by serverId, data._id, or data.id
    if (!existing) {
      const records = await store.getAllByOwner(owner);
      const matchesValue = (v) => v !== undefined && v !== null && String(v) === String(localId);
      existing = records.find(r =>
        matchesValue(r.serverId) ||
        matchesValue(r.data?._id) ||
        matchesValue(r.data?.id)
      ) || null;
      if (existing) localId = existing.localId;
    }

    if (!existing) return;

    const now = Date.now();

    await store.delete(localId);

    if (!options.skipQueue) {
      await SyncQueueStore.add({
        localId,
        entityType,
        action: 'delete',
        data: existing.data,
        version: existing.version || 1,
        serverId: existing.serverId,
        owner,
        status: 'pending',
        retryCount: 0,
        createdAt: now,
      });
    }
  }

  async markSynced(queueItem, serverId, serverVersion, resolvedId) {
    const { localId, entityType, action } = queueItem;

    if (action === 'delete') return;

    const store = this._getStore(entityType);

    // Remove the locally synced record — it is now persisted on the server
    // and will be re-cached by the fetch interceptor on the next GET.
    try {
      await store.delete(localId);
    } catch {}
  }

  async getPendingQueue() {
    const owner = this.getCurrentOwner();
    if (!owner) return [];
    try {
      const all = await SyncQueueStore.getAll();
      return all
        .filter(item => item.owner === owner && (item.status === 'pending' || item.status === 'failed'))
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    } catch (err) {
      logger.error('OfflineStorage.getPendingQueue failed', err, 'Offline');
      return [];
    }
  }

  async getQueueSummary() {
    const owner = this.getCurrentOwner();
    if (!owner) return { pending: 0, failed: 0, total: 0 };
    try {
      const all = await SyncQueueStore.getAll();
      const ownerItems = all.filter(item => item.owner === owner);
      return {
        pending: ownerItems.filter(i => i.status === 'pending').length,
        failed: ownerItems.filter(i => i.status === 'failed').length,
        total: ownerItems.length,
      };
    } catch {
      return { pending: 0, failed: 0, total: 0 };
    }
  }

  async removeFromQueue(queueItemId) {
    await SyncQueueStore.delete(queueItemId);
  }

  async updateQueueItem(queueItemId, updates) {
    const item = await SyncQueueStore.get(queueItemId);
    if (item) {
      await SyncQueueStore.add({ ...item, ...updates, updatedAt: Date.now() });
    }
  }

  async clearAll() {
    for (const entityType of ENTITY_LIST) {
      const store = ENTITY_STORES[entityType];
      try { await store.clear(); } catch {}
    }
    try { await SyncQueueStore.clear(); } catch {}
  }

  async clearForOwner() {
    const owner = this.getCurrentOwner();
    if (!owner) return;

    for (const entityType of ENTITY_LIST) {
      const store = ENTITY_STORES[entityType];
      try {
        const records = await store.getAllByOwner(owner);
        for (const rec of records) {
          await store.delete(rec.localId);
        }
      } catch {}
    }

    try {
      const all = await SyncQueueStore.getAll();
      for (const item of all) {
        if (item.owner === owner) {
          await SyncQueueStore.delete(item.id);
        }
      }
    } catch {}
  }

  async cacheServerData(entityType, serverRecords) {
    const owner = this.getCurrentOwner();
    if (!owner) return;

    const store = this._getStore(entityType);

    // Collect server IDs from the response
    const serverIds = new Set();
    for (const serverRec of serverRecords) {
      const serverId = serverRec._id || serverRec.id;
      if (serverId) {
        serverIds.add(serverId);
      }
    }

    // Upsert server records and track which localIds were used
    const upsertedLocalIds = new Set();
    for (const serverRec of serverRecords) {
      const serverId = serverRec._id || serverRec.id;
      if (!serverId) continue;

      const existing = await this.getByServerId(entityType, serverId);

      const localId = existing?.localId || serverId;
      upsertedLocalIds.add(localId);

      const record = {
        localId,
        owner,
        data: { ...serverRec },
        version: serverRec.version || 1,
        serverId,
        createdAt: existing?.createdAt || Date.now(),
        updatedAt: Date.now(),
        syncedAt: Date.now(),
      };

      await store.put(record);
    }

    const localRecords = await store.getAllByOwner(owner);
    const pendingItems = await SyncQueueStore.getAll();
    const pendingLocalIds = new Set(
      pendingItems
        .filter(item => item.owner === owner && (item.status === 'pending' || item.status === 'failed'))
        .map(item => item.localId)
    );

    // Build a map of seen server IDs (from data._id/data.id) to their localId
    const seenIds = new Map();
    for (const localRec of localRecords) {
      const recId = localRec.serverId || localRec.data?._id || localRec.data?.id;
      if (!recId) continue;

      const isUpserted = upsertedLocalIds.has(localRec.localId);
      const isPending = pendingLocalIds.has(localRec.localId);

      if (seenIds.has(recId)) {
        // Duplicate found — delete this one if it's not pending and not the upserted canonical
        if (!isPending && !isUpserted) {
          await store.delete(localRec.localId);
        }
      } else {
        seenIds.set(recId, { localId: localRec.localId, isUpserted, isPending });
      }
    }

    // Remove locally-cached records that were deleted on the server.
    // Only runs when server has data to compare against.
    if (serverIds.size > 0) {
      for (const localRec of localRecords) {
        if (
          localRec.serverId &&
          !serverIds.has(localRec.serverId) &&
          !pendingLocalIds.has(localRec.localId) &&
          !upsertedLocalIds.has(localRec.localId)
        ) {
          await store.delete(localRec.localId);
        }
      }
    }
  }
}

const offlineStorage = new OfflineStorage();
export default offlineStorage;
